import React, { useEffect, useRef } from 'react';
import { PhoneInput as BasePhoneInput } from 'react-international-phone';
import { CheckCircleIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import './PhoneInput.css';

export interface PhoneInputProps {
  value: string;
  onChange: (phone: string) => void;
  /** InlineEdit's global-edition mode commits on blur, so this has to reach the input. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  validated?: 'success' | 'error' | 'default';
  isDisabled?: boolean;
  isRequired?: boolean;
  id?: string;
  name?: string;
  /** Applied to the wrapper, alongside the component's own class. */
  className?: string;
  /** Labels the underlying input, for fields with no visible <label>. */
  ariaLabel?: string;
}

const STATUS_BORDER_COLOR: Record<string, string> = {
  error: 'var(--pf-t--global--border--color--status--danger--default)',
  success: 'var(--pf-t--global--border--color--status--success--default)',
};

/**
 * Whether two numbers differ only in how they are written.
 *
 * react-international-phone reformats whatever value it is handed as it mounts, and
 * reports that back through onChange - a change the user never made. A page tracking
 * unsaved edits would come up dirty the moment it loads a record, so use this to tell
 * that first call apart from a real edit.
 */
export const isSamePhoneNumber = (a?: string, b?: string) =>
  (a || '').replace(/\D/g, '') === (b || '').replace(/\D/g, '');

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  validated = 'default',
  isDisabled = false,
  isRequired = false,
  id,
  name,
  className,
  ariaLabel
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (validated === 'error' && containerRef.current) {
      containerRef.current.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(2px)' },
          { transform: 'translateX(-2px)' },
          { transform: 'translateX(0)' }
        ],
        { duration: 300, easing: 'ease-in-out' }
      );
    }
  }, [validated]);

  // Feeds the one hook PhoneInput.css leaves open, so the status colour reaches
  // the input, the country button and the dial-code preview together.
  const statusStyle = STATUS_BORDER_COLOR[validated]
    ? ({ '--niit-phone-input--BorderColor': STATUS_BORDER_COLOR[validated] } as React.CSSProperties)
    : undefined;

  return (
    <div
      ref={containerRef}
      className={className ? `niit-phone-input ${className}` : 'niit-phone-input'}
      style={statusStyle}
    >
      <BasePhoneInput
        value={value}
        name={name}
        inputProps={{
          id: id,
          required: isRequired,
          'aria-invalid': validated === 'error',
          'aria-label': ariaLabel,
          onBlur: onBlur
        }}
        onChange={onChange}
        disabled={isDisabled}
        inputStyle={{
          paddingRight: validated !== 'default' ? '32px' : undefined
        }}
      />

      {validated === 'error' && (
        <ExclamationCircleIcon
          color="var(--pf-t--global--icon--color--status--danger--default)"
          style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        />
      )}
      {validated === 'success' && (
        <CheckCircleIcon
          color="var(--pf-t--global--icon--color--status--success--default)"
          style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        />
      )}
    </div>
  );
};
